import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';

import {
  Paper,
  Grid,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';

import { Edit } from '@mui/icons-material';

import ButtonWithSpinner from '../ButtonWithSpinner';
import ConfirmationModal from '../ConfirmationModal';

import usePlans from '../../hooks/usePlans';
import { i18n } from '../../translate/i18n';
import { getStyled } from './styles';

export function PlanManagerForm({
  onSubmit,
  onDelete,
  onCancel,
  initialValue,
  loading,
}) {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [record, setRecord] = useState({
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    useOpenAi: true,
    isPublic: true,
  });

  useEffect(() => {
    setRecord(initialValue);
  }, [initialValue]);

  const handleSubmit = async (data) => {
    onSubmit(data);
  };

  return (
    <Formik
      enableReinitialize
      sx={classes.fullWidth}
      initialValues={record}
      onSubmit={(values, { resetForm }) =>
        setTimeout(() => {
          handleSubmit(values);
          resetForm();
        }, 500)
      }
    >
      {() => (
        <Form sx={classes.fullWidth}>
          <Grid spacing={1} justifyContent='flex-start' container>
            <Grid size={{ xs: 12, sm: 6, md: 2, xl: 2 }}>
              <Field
                as={TextField}
                label={i18n.t('plans.form.name')}
                name='name'
                variant='outlined'
                sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                margin='dense'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2, xl: 2 }}>
              <FormControl margin='dense' variant='outlined' fullWidth>
                <InputLabel htmlFor='status-selection' sx={theme.custom.label}>
                  {i18n.t('plans.form.public')}
                </InputLabel>
                <Field
                  as={Select}
                  id='status-selection'
                  label={i18n.t('plans.form.public')}
                  labelId='status-selection-label'
                  name='isPublic'
                  margin='dense'
                  sx={theme.custom.selectField}
                  MenuProps={theme.custom.menuProps}
                >
                  <MenuItem value={true}>Sim</MenuItem>
                  <MenuItem value={false}>Não</MenuItem>
                </Field>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <Field
                as={TextField}
                label={i18n.t('plans.form.users')}
                name='users'
                variant='outlined'
                sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                margin='dense'
                type='number'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <Field
                as={TextField}
                label={i18n.t('plans.form.connections')}
                name='connections'
                variant='outlined'
                sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                margin='dense'
                type='number'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <Field
                as={TextField}
                label='Filas'
                name='queues'
                variant='outlined'
                sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                margin='dense'
                type='number'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1 }}>
              <Field
                as={TextField}
                label='Valor'
                name='amount'
                variant='outlined'
                sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                margin='dense'
                type='text'
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl margin='dense' variant='outlined' fullWidth>
                <InputLabel
                  htmlFor='useWhatsapp-selection'
                  sx={theme.custom.label}
                >
                  Whatsapp
                </InputLabel>
                <Field
                  as={Select}
                  id='useWhatsapp-selection'
                  label='Whatsapp'
                  labelId='useWhatsapp-selection-label'
                  name='useWhatsapp'
                  margin='dense'
                  sx={theme.custom.selectField}
                  MenuProps={theme.custom.menuProps}
                >
                  <MenuItem value={true}>
                    {i18n.t('plans.form.enabled')}
                  </MenuItem>
                  <MenuItem value={false}>
                    {i18n.t('plans.form.disabled')}
                  </MenuItem>
                </Field>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl margin='dense' variant='outlined' fullWidth>
                <InputLabel
                  htmlFor='useFacebook-selection'
                  sx={theme.custom.label}
                >
                  Facebook
                </InputLabel>
                <Field
                  as={Select}
                  id='useFacebook-selection'
                  label='Facebook'
                  labelId='useFacebook-selection-label'
                  name='useFacebook'
                  margin='dense'
                  sx={theme.custom.selectField}
                  MenuProps={theme.custom.menuProps}
                >
                  <MenuItem value={true}>
                    {i18n.t('plans.form.enabled')}
                  </MenuItem>
                  <MenuItem value={false}>
                    {i18n.t('plans.form.disabled')}
                  </MenuItem>
                </Field>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl margin='dense' variant='outlined' fullWidth>
                <InputLabel
                  htmlFor='useInstagram-selection'
                  sx={theme.custom.label}
                >
                  Instagram
                </InputLabel>
                <Field
                  as={Select}
                  id='useInstagram-selection'
                  label='Instagram'
                  labelId='useInstagram-selection-label'
                  name='useInstagram'
                  margin='dense'
                  sx={theme.custom.selectField}
                  MenuProps={theme.custom.menuProps}
                >
                  <MenuItem value={true}>
                    {i18n.t('plans.form.enabled')}
                  </MenuItem>
                  <MenuItem value={false}>
                    {i18n.t('plans.form.disabled')}
                  </MenuItem>
                </Field>
              </FormControl>
            </Grid>
          </Grid>
          <Grid spacing={2} justifyContent='flex-end' container>
            <Grid size={{ sm: 3, md: 2 }}>
              <ButtonWithSpinner
                sx={{ ...classes.fullWidth, ...theme.custom.cancelButton }}
                loading={loading}
                onClick={() => onCancel()}
                variant='outlined'
              >
                {i18n.t('plans.form.clear')}
              </ButtonWithSpinner>
            </Grid>
            {record.id !== undefined ? (
              <Grid size={{ sm: 3, md: 2 }}>
                <ButtonWithSpinner
                  sx={{ ...classes.fullWidth, ...theme.custom.cancelButton }}
                  loading={loading}
                  onClick={() => onDelete(record)}
                  variant='outlined'
                >
                  {i18n.t('plans.form.delete')}
                </ButtonWithSpinner>
              </Grid>
            ) : null}
            <Grid size={{ sm: 3, md: 2 }}>
              <ButtonWithSpinner
                sx={{ ...classes.fullWidth, ...theme.custom.confirmButton }}
                loading={loading}
                type='submit'
                variant='outlined'
              >
                {i18n.t('plans.form.save')}
              </ButtonWithSpinner>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

export function PlansManagerGrid({ records, onSelect }) {
  const theme = useTheme();
  const classes = getStyled(theme);

  const renderWhatsapp = (row) => {
    return row.useWhatsapp === false
      ? `${i18n.t('plans.form.no')}`
      : `${i18n.t('plans.form.yes')}`;
  };

  const renderFacebook = (row) => {
    return row.useFacebook === false
      ? `${i18n.t('plans.form.no')}`
      : `${i18n.t('plans.form.yes')}`;
  };

  const renderInstagram = (row) => {
    return row.useInstagram === false
      ? `${i18n.t('plans.form.no')}`
      : `${i18n.t('plans.form.yes')}`;
  };

  return (
    <Paper sx={classes.tableContainer}>
      <Table sx={classes.fullWidth} padding='none' aria-label='a dense table'>
        <TableHead>
          <TableRow>
            <TableCell align='center' sx={{ width: '1%' }}>
              #
            </TableCell>
            <TableCell align='left'>{i18n.t('plans.form.name')}</TableCell>
            <TableCell align='center'>{i18n.t('plans.form.users')}</TableCell>
            <TableCell align='center'>{i18n.t('plans.form.public')}</TableCell>
            <TableCell align='center'>
              {i18n.t('plans.form.connections')}
            </TableCell>
            <TableCell align='center'>Filas</TableCell>
            <TableCell align='center'>Valor</TableCell>
            <TableCell align='center'>Whatsapp</TableCell>
            <TableCell align='center'>Facebook</TableCell>
            <TableCell align='center'>Instagram</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row) => (
            <TableRow key={row.id}>
              <TableCell align='center' sx={{ width: '1%' }}>
                <IconButton onClick={() => onSelect(row)} aria-label='delete'>
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell align='left'>{row.name || '-'}</TableCell>
              <TableCell align='center'>{row.users || '-'}</TableCell>
              <TableCell align='center'>
                {(row.isPublic ? 'Sim' : 'Não') || '-'}
              </TableCell>
              <TableCell align='center'>{row.connections || '-'}</TableCell>
              <TableCell align='center'>{row.queues || '-'}</TableCell>
              <TableCell align='center'>
                {i18n.t('plans.form.money')}{' '}
                {row.amount
                  ? row.amount.toLocaleString('pt-br', {
                      minimumFractionDigits: 2,
                    })
                  : '00.00'}
              </TableCell>
              <TableCell align='center'>{renderWhatsapp(row)}</TableCell>
              <TableCell align='center'>{renderFacebook(row)}</TableCell>
              <TableCell align='center'>{renderInstagram(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function PlansManager() {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { list, save, update, remove } = usePlans();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    isPublic: true,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const planList = await list();
        if (isMounted) {
          setRecords(planList);
        }
      } catch {
        if (isMounted) {
          toastError(`Não foi possível carregar a lista de registros.`);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [list]);

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      const planList = await list();
      setRecords(planList);
      handleCancel();
      toastSuccess('Operação realizada com sucesso!');
    } catch {
      toastError(
        `Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente.`,
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      const planList = await list();
      setRecords(planList);
      handleCancel();
      toastSuccess('Operação realizada com sucesso!');
    } catch {
      toastError('Não foi possível realizar a operação');
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      id: undefined,
      name: '',
      users: 0,
      connections: 0,
      queues: 0,
      amount: 0,
      useWhatsapp: true,
      useFacebook: true,
      useInstagram: true,
      isPublic: true,
    });
  };

  const handleSelect = (data) => {
    let useWhatsapp = data.useWhatsapp === false ? false : true;
    let useFacebook = data.useFacebook === false ? false : true;
    let useInstagram = data.useInstagram === false ? false : true;

    setRecord({
      id: data.id,
      name: data.name || '',
      users: data.users || 0,
      connections: data.connections || 0,
      queues: data.queues || 0,
      amount:
        data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
      useWhatsapp,
      useFacebook,
      useInstagram,
      isPublic: data.isPublic,
    });
  };

  return (
    <Paper sx={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid size={{ xs: 12 }}>
          <PlanManagerForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PlansManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title='Exclusão de Registro'
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </Paper>
  );
}
